import { useEffect, useRef } from 'react';

/**
 * Answers a shell transition attempt before the shell acts on it. Returns `null` to let it through
 * untouched, or a promise of whether to go ahead. For a page that asks with its own prompt, so can't
 * be a `useNavigationGuard`: the shell unmounts the page as soon as it confirms a move to another
 * app, so a router blocker on that page would be torn down mid-prompt.
 *
 * Kept out of `./index` (as is the pending URL) because many tests mock that module with a factory,
 * and `safeNavigate` and the editor's leave checks run in them.
 */
export type NavigationInterceptor = (url: string | null) => Promise<boolean> | null;

const interceptors = new Set<NavigationInterceptor>();

export function getNavigationInterceptors(): NavigationInterceptor[] {
  return [...interceptors];
}

/**
 * Registers `interceptor` while mounted. `NavigationGuardProvider` asks it on each shell transition
 * attempt; without a provider nothing does.
 */
export function useNavigationInterceptor(interceptor: NavigationInterceptor) {
  const interceptorRef = useRef(interceptor);
  interceptorRef.current = interceptor;

  useEffect(() => {
    // Answers still open when this unmounts. A page can go away mid-prompt (e.g. browser Back while
    // its modal is open) without the prompt ever settling, and the provider waits for one answer at a
    // time, so an answer left open would block every later shell navigation.
    const openAnswers = new Set<(shouldProceed: boolean) => void>();

    const registered: NavigationInterceptor = (url) => {
      const answer = interceptorRef.current(url);
      if (answer == null) {
        return null;
      }

      return new Promise<boolean>((resolve) => {
        openAnswers.add(resolve);
        answer.then(resolve, () => resolve(false)).finally(() => openAnswers.delete(resolve));
      });
    };
    interceptors.add(registered);

    return () => {
      interceptors.delete(registered);
      for (const resolve of openAnswers) {
        resolve(false);
      }
      openAnswers.clear();
    };
  }, []);
}

/**
 * Where the shell transition being attempted is headed. `safeNavigate` records it just before it
 * announces the attempt, and `NavigationGuardProvider` takes it when the attempt arrives.
 */
let pendingTransitionUrl: string | null = null;

export function setPendingTransitionUrl(url: string | null) {
  pendingTransitionUrl = url;
}

export function takePendingTransitionUrl(): string | null {
  const url = pendingTransitionUrl;
  pendingTransitionUrl = null;
  return url;
}
